import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Modal,
  ModalBody,
  ModalHeader,
  Slider,
  Slide,
  useSlider,
  OverflowText,
  Breadcrumb,
  ModalFooter,
} from "@transfr-inc/dashboard-components";
import {
  SearchBox,
  useSearch,
  Button,
} from "@transfr-inc/dashboard-components/forms";
import "./export-awards.modal.scss";
import { LearnerAwardsSlide } from "./learner-awards-slide.component";
import { handleAwardSelection, handleOpenAllAwards } from "./awards.utils";
import { getLearnerTerm } from "@transfr-inc/dashboard-components/constants";

const ExportAwardsTitle = "Export Credentials";
const AwardsTitle = "Credentials:";

const getFullName = (user) => `${user.firstName} ${user.lastName}`.trim();

export const ExportAwardsModal = ({ onClose, users, open, awardsLinksMap }) => {
  const totalSlides = 2;
  const slider = useSlider({ totalSlides });
  const [notification, setNotification] = useState();
  const [learners, setLearners] = useState(users);
  const [selectedLearner, setSelectedLearner] = useState();
  const [modalTitle, setModalTitle] = useState(ExportAwardsTitle);

  const onLearnerSelected = (learner) => {
    setSelectedLearner(learner);
    setModalTitle(`${AwardsTitle} ${learner.firstName} ${learner.lastName}`);
    slider.nextSlide();
  };

  const learnersSearch = useSearch({
    data: learners,
    searchKeys: ["firstName", "lastName"],
  });

  const onLearnerTermChange = (term) => {
    const results = learnersSearch.queryData(term);
    setLearners(results);
  };

  const onLearnerSearchSelected = (user) => {
    setLearners([user]);
  };

  const goExportAwardsSlide = () => {
    setSelectedLearner();
    setLearners();
    setModalTitle(ExportAwardsTitle);
    slider.previousSlide();
  };

  const onAwardSelected = (award) => {
    if (!awardsLinksMap || !award?.groupId || !selectedLearner?.userId) return;
    const link = awardsLinksMap?.[selectedLearner?.userId]?.[award.groupId];
    handleAwardSelection(award, link, setNotification);
  };

  const openAllAwards = () => {
    if (!selectedLearner?.userId || !awardsLinksMap?.[selectedLearner.userId]) {
      handleOpenAllAwards([], setNotification);
      return;
    }
    const links = Object.values(awardsLinksMap[selectedLearner.userId]);
    handleOpenAllAwards(links, setNotification);
  };

  useEffect(() => {
    if (users) {
      const results = learnersSearch.updateData(users);
      setLearners(results);
    }
  }, [users]);

  const modalTitleElement = (
    <>
      {selectedLearner && (
        <Breadcrumb onClick={goExportAwardsSlide} label="All Learners" />
      )}
      <div className="title-container">
        <h2>{modalTitle}</h2>
      </div>
    </>
  );

  return (
    <Modal open={open} onClose={onClose} modalClassName="export-awards-modal">
      <ModalHeader
        className="export-awards-modal-header"
        title={modalTitleElement}
        icon={!selectedLearner && ["fa-regular", "file-export"]}
      ></ModalHeader>
      <ModalBody>
        <Slider
          className="export-awards-modal-body"
          totalSlides={totalSlides}
          currentSlideIndex={slider.currentIndex}
        >
          <Slide>
            <div className="export-awards-slide">
              <h3>{getLearnerTerm({ plural: true })}</h3>
              <SearchBox
                disabled={!learners}
                placeholder="Search"
                query={learnersSearch.queryData}
                onSearchTermChange={onLearnerTermChange}
                onResultSelected={onLearnerSearchSelected}
                customOptionLabel={getFullName}
              />
              <div className="export-awards-modal-content">
                <div className="modal-body-list-container">
                  <div>
                    {learners?.map((learner) => (
                      <button
                        className="learner-item"
                        key={learner.userId}
                        onClick={() => onLearnerSelected(learner)}
                      >
                        <div className="learner-item-content">
                          <div className="learner-item-info">
                            <OverflowText
                              className="main-title"
                              text={learner.lastName + ", " + learner.firstName}
                            >
                              {learner.lastName + ", " + learner.firstName}
                            </OverflowText>
                          </div>
                        </div>
                        <FontAwesomeIcon icon="fa-light fa-chevron-right" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Slide>
          <Slide>
            <LearnerAwardsSlide
              awards={selectedLearner?.awards}
              notification={notification}
              onNotificationClose={() => setNotification()}
              onAwardSelected={onAwardSelected}
              showOpenAllButton={true}
            />
          </Slide>
        </Slider>
      </ModalBody>
      <ModalFooter>
        {slider.currentIndex === 1 && (
          <div className="learner-awards-footer">
            <Button primary onClick={openAllAwards}>
              Open All
            </Button>
          </div>
        )}
      </ModalFooter>
    </Modal>
  );
};
